import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import SkillIcon from '@atlaskit/icon-lab/core/skill';
import Lozenge from '@atlaskit/lozenge';
import { Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { HoverCard } from '@atlassian/rovo-platform-ui-components/hover-card';

import { messages } from '../messages';
const MAX_LIST_ITEMS = 9;
const MAX_FULLSCREEN_SKILLS = 8;
const VIEW_ALL_SKILLS_ID = '__view_all_skills__';
const ITEM_HEIGHT_PX = 56;
const LIST_ITEM_GAP_PX = 12;
const ROW_HEIGHT_PX = ITEM_HEIGHT_PX + LIST_ITEM_GAP_PX;

const listStyles = cssMap({
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
		height: '100%',
		width: '100%',
		overflowX: 'hidden',
		paddingLeft: token('space.250'),
		paddingRight: token('space.250'),
	},
	wrapperFullscreen: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		width: '100%',
		maxWidth: 'min(766px, 95vw)',
		marginLeft: 'auto',
		marginRight: 'auto',
		overflowX: 'hidden',
		overflowY: 'visible',
		paddingBottom: token('space.100'),
	},
	titleSection: {
		paddingLeft: token('space.0'),
		marginTop: token('space.300'),
		marginBottom: token('space.100'),
	},
	itemsContainer: {
		overflowX: 'visible',
		overflowY: 'visible',
		flexShrink: 0,
	},
	itemsList: {
		overflowX: 'hidden',
		overflowY: 'hidden',
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		gap: token('space.050'),
		listStyle: 'none',
		marginTop: token('space.0'),
	},
	itemsListFullscreen: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: token('space.150'),
		paddingLeft: token('space.0'),
		paddingRight: token('space.0'),
		listStyle: 'none',
		marginTop: token('space.0'),
	},
	itemButton: {
		width: '100%',
		minHeight: '50px',
		paddingTop: token('space.075'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.075'),
		paddingLeft: token('space.150'),
		backgroundColor: token('color.background.neutral.subtle'),
		textAlign: 'left',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			borderRadius: token('radius.small'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	itemButtonSelected: {
		backgroundColor: token('color.background.neutral.subtle.hovered'),
		borderRadius: token('radius.small'),
	},
	itemInner: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		width: '100%',
	},
	itemContent: {
		flex: 1,
		minWidth: '0px',
	},
	itemIconContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		flexShrink: 0,
		color: token('color.text.subtle'),
		backgroundColor: token('elevation.surface'),
		borderRadius: token('radius.medium'),
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
	descriptionExpandable: {
		display: 'grid',
		overflow: 'hidden',
		transition: `grid-template-rows 100ms cubic-bezier(0.2, 0, 0, 1)`,
	},
	descriptionExpandableInner: {
		minHeight: '0px',
		overflow: 'hidden',
	},
});

export interface AutoSuggestItem {
	id: string;
	name: string;
	description?: string;
	icon?: React.ReactNode;
}

export interface AutoSuggestScreenProps {
	items: AutoSuggestItem[];
	searchQuery: string;
	onSelectItem: (item: AutoSuggestItem) => void;
	onDismiss?: () => void;
	onItemHover?: (item: AutoSuggestItem | null) => void;
	onViewAllSkills?: () => void;
	renderHoverCardContent?: (item: AutoSuggestItem) => React.ReactNode;
	layout?: 'sidebar' | 'fullscreen';
}

const filterItems = (items: AutoSuggestItem[], searchValue: string): AutoSuggestItem[] => {
	if (!searchValue) return items;
	const search = searchValue.toLowerCase();
	return items.filter(
		(item) =>
			item.name.toLowerCase().includes(search) || item.description?.toLowerCase().includes(search),
	);
};

interface ItemRowProps {
	item: AutoSuggestItem;
	isSelected: boolean;
	shortcutLabel?: string;
	onClick: () => void;
	onHover?: (item: AutoSuggestItem | null) => void;
	isViewAll?: boolean;
	isHoveredFromParent?: boolean;
}

const ItemRow = ({
	item,
	isSelected,
	shortcutLabel,
	onClick,
	onHover,
	isViewAll,
	isHoveredFromParent,
}: ItemRowProps) => {
	const [isHoveredLocal, setIsHoveredLocal] = useState(false);
	const showHoverStyle = isHoveredFromParent ?? isHoveredLocal;
	const handleMouseEnter = useCallback(() => {
		setIsHoveredLocal(true);
		onHover?.(item);
	}, [item, onHover]);
	const handleMouseLeave = useCallback(() => {
		setIsHoveredLocal(false);
		onHover?.(null);
	}, [onHover]);
	const handleMouseDown = (event: React.MouseEvent) => event.preventDefault();

	return (
		<Pressable
			xcss={cx(
				listStyles.itemButton,
				(showHoverStyle || isSelected) && listStyles.itemButtonSelected,
			)}
			onClick={onClick}
			onMouseDown={handleMouseDown}
			role="option"
			aria-selected={isSelected}
			testId={isViewAll ? 'auto-suggest-view-all-skills' : `auto-suggest-item-${item.id}`}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleMouseEnter}
			onBlur={handleMouseLeave}
		>
			<Box xcss={listStyles.itemInner}>
				<Box xcss={listStyles.itemIconContainer}>{item.icon}</Box>
				<Box xcss={listStyles.itemContent}>
					<Stack space="space.025">
						<Text>{item.name}</Text>
						{item.description && (
							<Box
								xcss={listStyles.descriptionExpandable}
								style={{ gridTemplateRows: showHoverStyle || isSelected ? '1fr' : '0fr' }}
							>
								<Box xcss={listStyles.descriptionExpandableInner}>
									<Text size="small" color="color.text.subtlest" maxLines={1}>
										{item.description}
									</Text>
								</Box>
							</Box>
						)}
					</Stack>
				</Box>
				{shortcutLabel && <Lozenge appearance="neutral">{shortcutLabel}</Lozenge>}
			</Box>
		</Pressable>
	);
};

const getShortcutLabel = (index: number): string => {
	const mod = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '⌘' : 'Ctrl';
	return `${mod}${index + 1}`;
};

export const AutoSuggestScreen = ({
	items,
	searchQuery,
	onSelectItem,
	onDismiss,
	onItemHover,
	onViewAllSkills,
	renderHoverCardContent,
	layout = 'sidebar',
}: AutoSuggestScreenProps): JSX.Element => {
	const isFullscreen = layout === 'fullscreen';
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const title = formatMessage(messages.title);
	const viewAllPlaceholderItem = useMemo(
		(): AutoSuggestItem => ({
			id: VIEW_ALL_SKILLS_ID,
			name: formatMessage(messages.viewAllSkills),
			description: undefined,
			icon: <SkillIcon label="" />,
		}),
		[formatMessage],
	);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
	const [wrapperHeight, setWrapperHeight] = useState<number | null>(null);
	const wrapperRef = React.useRef<HTMLDivElement>(null);

	const filteredItems = useMemo(() => filterItems(items, searchQuery), [items, searchQuery]);
	const maxVisibleTotal = useMemo(() => {
		if (isFullscreen) return MAX_FULLSCREEN_SKILLS;
		if (wrapperHeight == null) return MAX_LIST_ITEMS;
		const fit = Math.floor(wrapperHeight / ROW_HEIGHT_PX);
		return Math.min(MAX_LIST_ITEMS, Math.max(1, fit));
	}, [wrapperHeight, isFullscreen]);
	const maxVisibleSkills = onViewAllSkills ? maxVisibleTotal - 1 : maxVisibleTotal;
	const visibleSkillItems = useMemo(
		() => filteredItems.slice(0, maxVisibleSkills),
		[filteredItems, maxVisibleSkills],
	);
	const listItems = useMemo(() => {
		if (onViewAllSkills && visibleSkillItems.length > 0) {
			return [...visibleSkillItems, viewAllPlaceholderItem];
		}
		return visibleSkillItems;
	}, [visibleSkillItems, onViewAllSkills, viewAllPlaceholderItem]);

	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'rovoChatPromptTypeAhead',
		});
	}, [sendAnalyticsEvent]);

	useEffect(() => {
		if (isFullscreen) return;
		const el = wrapperRef.current;
		if (!el) return;
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (entry) setWrapperHeight(entry.contentRect.height);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, [isFullscreen]);
	useEffect(() => {
		setSelectedIndex(-1);
		setHoveredIndex(null);
	}, [searchQuery]);
	useEffect(() => {
		if (listItems.length === 0) {
			setSelectedIndex(-1);
			setHoveredIndex(null);
		}
	}, [listItems.length]);

	const handleSelectIndex = useCallback(
		(index: number) => {
			const item = listItems[index];
			if (item.id === VIEW_ALL_SKILLS_ID) onViewAllSkills?.();
			else onSelectItem(item);
		},
		[listItems, onSelectItem, onViewAllSkills],
	);

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (listItems.length === 0) return;
			if ((event.metaKey || event.ctrlKey) && event.key >= '1' && event.key <= '9') {
				const index = parseInt(event.key, 10) - 1;
				if (index < listItems.length) {
					event.preventDefault();
					event.stopPropagation();
					handleSelectIndex(index);
				} else if (
					event.key === '9' &&
					listItems.length > 0 &&
					listItems[listItems.length - 1].id === VIEW_ALL_SKILLS_ID
				) {
					event.preventDefault();
					event.stopPropagation();
					onViewAllSkills?.();
				}
				return;
			}
			if (event.key === 'ArrowDown') {
				event.preventDefault();
				event.stopPropagation();
				setSelectedIndex((prev) => (prev < listItems.length - 1 ? prev + 1 : 0));
			} else if (event.key === 'ArrowUp') {
				event.preventDefault();
				event.stopPropagation();
				setSelectedIndex((prev) => (prev > 0 ? prev - 1 : listItems.length - 1));
			} else if (event.key === 'Enter' && selectedIndex >= 0) {
				event.preventDefault();
				event.stopPropagation();
				handleSelectIndex(selectedIndex);
			} else if (event.key === 'Escape') {
				event.stopPropagation();
				setSelectedIndex(-1);
				onDismiss?.();
			}
		};
		return bind(document, { type: 'keydown', listener: handleKeyDown, options: { capture: true } });
	}, [listItems, selectedIndex, handleSelectIndex, onViewAllSkills, onDismiss]);

	return (
		<Box
			ref={wrapperRef}
			xcss={isFullscreen ? listStyles.wrapperFullscreen : listStyles.wrapper}
			testId="auto-suggest-skills-screen"
		>
			{title && (
				<Box xcss={listStyles.titleSection}>
					<Text size="small" weight="medium" color="color.text.subtle">
						{title}
					</Text>
				</Box>
			)}
			<Stack space="space.150" xcss={listStyles.itemsContainer}>
				<Box
					xcss={isFullscreen ? listStyles.itemsListFullscreen : listStyles.itemsList}
					role="listbox"
					aria-label={title}
				>
					{listItems.length > 0 &&
						listItems.map((item, index) => {
							const isViewAll = item.id === VIEW_ALL_SKILLS_ID;
							const isSkillRow = !isViewAll;
							const isSelected = index === selectedIndex;
							const row = (
								<ItemRow
									item={item}
									isSelected={isSelected}
									shortcutLabel={isViewAll ? getShortcutLabel(8) : getShortcutLabel(index)}
									onClick={() => handleSelectIndex(index)}
									onHover={isSkillRow ? onItemHover : undefined}
									isViewAll={isViewAll}
									isHoveredFromParent={
										isSkillRow && renderHoverCardContent ? hoveredIndex === index : undefined
									}
								/>
							);
							const wrapped =
								isSkillRow && renderHoverCardContent ? (
									<Box
										onMouseEnter={() => setHoveredIndex(index)}
										onMouseLeave={() => setHoveredIndex(null)}
									>
										<HoverCard
											content={renderHoverCardContent(item)}
											placement="left"
											fallbackPlacements={['right', 'bottom', 'top']}
											testId={`auto-suggest-item-${item.id}--hovercard`}
										>
											{row}
										</HoverCard>
									</Box>
								) : (
									row
								);
							return <React.Fragment key={item.id}>{wrapped}</React.Fragment>;
						})}
				</Box>
			</Stack>
		</Box>
	);
};
